import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, CheckCircle, User, Phone, Mail, MapPin, Calendar, Gauge, Camera, ArrowRight, DollarSign, FileText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SellYourCar = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    // Contact
    name: '',
    email: '',
    phone: '',
    province: '',
    city: '',
    // Vehicle
    make: '',
    model: '',
    variant: '',
    year: '',
    mileage: '',
    transmission: '',
    fuelType: '',
    color: '',
    condition: '',
    serviceHistory: '',
    askingPrice: '',
    // Additional
    description: '',
    reasonForSelling: '',
    contactPreference: 'whatsapp'
  });

  useEffect(() => {
    document.title = 'Sell Your Car | AutoSearch SA';
  }, []);

  const makes = ['Toyota', 'Volkswagen', 'Ford', 'BMW', 'Mercedes-Benz', 'Audi', 'Hyundai', 'Kia', 'Nissan', 'Mazda', 'Honda', 'Suzuki', 'Isuzu', 'Haval', 'GWM', 'Chery', 'BYD', 'Renault', 'Peugeot', 'Jeep', 'Land Rover', 'Volvo', 'Porsche', 'Jaguar', 'Mini', 'Opel', 'Mitsubishi', 'Other'];
  const provinces = ['Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape', 'Free State', 'Mpumalanga', 'Limpopo', 'North West', 'Northern Cape'];
  const years = Array.from({ length: 30 }, (_, i) => 2025 - i);
  const colors = ['White', 'Black', 'Silver', 'Grey', 'Blue', 'Red', 'Brown', 'Green', 'Gold', 'Orange', 'Other'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        lead_type: 'sell_your_car',
        contact_preference: form.contactPreference,
        car_info: {
          make: form.make,
          model: form.model,
          variant: form.variant,
          year: form.year,
          mileage: form.mileage,
          askingPrice: form.askingPrice
        },
        message: `SELL YOUR CAR Request:

VEHICLE DETAILS:
${form.year} ${form.make} ${form.model} ${form.variant}
Mileage: ${form.mileage} km
Color: ${form.color}
Transmission: ${form.transmission}
Fuel: ${form.fuelType}
Condition: ${form.condition}
Service History: ${form.serviceHistory}
Asking Price: R${form.askingPrice}

SELLER LOCATION:
${form.city}, ${form.province}

Reason for Selling: ${form.reasonForSelling}
Description: ${form.description}`
      });

      setSubmitted(true);
    } catch (e) {
      console.error('Submission failed:', e);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md text-center shadow-lg">
          <div className="w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Listing Submitted!</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            We've received your vehicle details. Our team will review your listing and contact you within 24-48 hours with next steps.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero */}
      <section className="bg-gradient-to-br from-green-600 via-green-700 to-teal-700 text-white py-12">
        <div className="container">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <DollarSign className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold">Sell Your Vehicle</h1>
                <p className="text-green-200">Get the best price for your car</p>
              </div>
            </div>
            <p className="text-lg text-green-100 mt-4">
              List your vehicle with us and reach thousands of potential buyers. We connect sellers with verified dealers and private buyers across South Africa.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="container py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: '🚗', title: 'Free Listing', desc: 'No upfront fees' },
            { icon: '👥', title: 'Wide Reach', desc: 'Thousands of buyers' },
            { icon: '🔒', title: 'Safe & Secure', desc: 'Verified contacts' },
            { icon: '⚡', title: 'Quick Sale', desc: 'Avg. 7-14 days' }
          ].map((item, idx) => (
            <div key={idx} className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-sm">
              <div className="text-3xl mb-2">{item.icon}</div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{item.title}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="container pb-12">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition ${
                  step >= s ? 'bg-green-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}>
                  {s}
                </div>
                {s < 3 && <div className={`w-16 h-1 mx-1 ${step > s ? 'bg-green-600' : 'bg-gray-200 dark:bg-gray-700'}`}></div>}
              </div>
            ))}
          </div>

          {/* Step 1: Contact Details */}
          {step === 1 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                <User className="w-5 h-5 text-green-600" />
                Your Contact Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="082 123 4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Province *</label>
                  <select
                    required
                    value={form.province}
                    onChange={(e) => setForm({ ...form, province: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Province</option>
                    {provinces.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City/Area</label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Sandton, Cape Town CBD"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition flex items-center gap-2"
                >
                  Next: Vehicle Details
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Vehicle Details */}
          {step === 2 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                <Car className="w-5 h-5 text-green-600" />
                Vehicle Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Make *</label>
                  <select
                    required
                    value={form.make}
                    onChange={(e) => setForm({ ...form, make: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Make</option>
                    {makes.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Model *</label>
                  <input
                    type="text"
                    required
                    value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Hilux, Polo, Ranger"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Variant</label>
                  <input
                    type="text"
                    value={form.variant}
                    onChange={(e) => setForm({ ...form, variant: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., 2.8 GD-6 Legend 50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Year *</label>
                  <select
                    required
                    value={form.year}
                    onChange={(e) => setForm({ ...form, year: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Year</option>
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mileage (km) *</label>
                  <input
                    type="number"
                    required
                    value={form.mileage}
                    onChange={(e) => setForm({ ...form, mileage: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., 85000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Color</label>
                  <select
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Color</option>
                    {colors.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Transmission</label>
                  <select
                    value={form.transmission}
                    onChange={(e) => setForm({ ...form, transmission: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select</option>
                    <option value="automatic">Automatic</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fuel Type</label>
                  <select
                    value={form.fuelType}
                    onChange={(e) => setForm({ ...form, fuelType: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select</option>
                    <option value="petrol">Petrol</option>
                    <option value="diesel">Diesel</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="electric">Electric</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Condition</label>
                  <select
                    value={form.condition}
                    onChange={(e) => setForm({ ...form, condition: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="needs_work">Needs Work</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-between">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition flex items-center gap-2"
                >
                  Next: Pricing
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Pricing & Submit */}
          {step === 3 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Pricing & Additional Info
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Asking Price (R) *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                    <input
                      type="number"
                      required
                      value={form.askingPrice}
                      onChange={(e) => setForm({ ...form, askingPrice: e.target.value })}
                      className="w-full pl-8 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., 350000"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Service History</label>
                  <select
                    value={form.serviceHistory}
                    onChange={(e) => setForm({ ...form, serviceHistory: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select</option>
                    <option value="full_agent">Full Agent History</option>
                    <option value="full_independent">Full Independent History</option>
                    <option value="partial">Partial History</option>
                    <option value="none">No Service History</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason for Selling</label>
                  <select
                    value={form.reasonForSelling}
                    onChange={(e) => setForm({ ...form, reasonForSelling: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select</option>
                    <option value="upgrading">Upgrading</option>
                    <option value="downsizing">Downsizing</option>
                    <option value="relocating">Relocating</option>
                    <option value="financial">Financial Reasons</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Preferred Contact</label>
                  <div className="flex gap-2">
                    {['whatsapp', 'call', 'email'].map((method) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => setForm({ ...form, contactPreference: method })}
                        className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition ${
                          form.contactPreference === method
                            ? 'bg-green-600 text-white border-green-600'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600'
                        }`}
                      >
                        {method === 'whatsapp' && '💬'}
                        {method === 'call' && '📞'}
                        {method === 'email' && '📧'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vehicle Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white resize-none"
                    placeholder="Describe your vehicle's condition, features, recent services, modifications, etc."
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-between">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-8 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition flex items-center gap-2 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Listing
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default SellYourCar;
